import { MatterRowContextMenu } from "@/components/features/matters/MatterRowContextMenu";
import type { ReactNode } from "react";
import {
  DataTable,
  DataTableColumnHeader,
  type DataTableColumnMeta,
  type DataTableServerPagination,
  TableCell,
  TableRow,
} from "@/components/ui";
import type { tanstackTableNames } from "@/lib/data/static/tanstack-table-names";
import type { ColumnDef } from "@tanstack/react-table";

export type ReportColumn<T> = {
  label: string;
  render: (row: T) => ReactNode;
  value?: (row: T) => unknown;
  numeric?: boolean;
};
export function ReportTable<T>({
  columns,
  rows,
  rowKey,
  total,
  isLoading,
  tableName,
  serverPagination,
  getMatterId,
  noResultsMessage = "No matters match these filters.",
}: {
  columns: ReportColumn<T>[];
  rows: T[];
  getMatterId?: (row: T) => string | undefined;
  rowKey: (row: T) => string;
  total?: T;
  isLoading?: boolean;
  tableName: (typeof tanstackTableNames)[keyof typeof tanstackTableNames];
  serverPagination?: DataTableServerPagination;
  noResultsMessage?: string;
}) {
  const dataTableColumns: ColumnDef<T>[] = columns.map((column) => ({
    id: column.label,
    accessorFn: column.value,
    enableSorting: !serverPagination && Boolean(column.value),
    meta: {
      cellClassName: "py-3 pr-4 pl-10",
      headerAlign: "start",
    } satisfies DataTableColumnMeta,
    header: ({ column: tableColumn }) => (
      <DataTableColumnHeader column={tableColumn} />
    ),
    cell: ({ row }) => (
      <div className={column.numeric ? "tabular-nums" : undefined}>
        {column.render(row.original)}
      </div>
    ),
  }));
  const renderTotal = (visibleColumnIds: string[]) => {
    if (!total) return null;
    const columnsById = new Map(
      columns.map((column) => [column.label, column]),
    );
    return (
      <TableRow className="bg-muted/50 hover:bg-muted/50 border-t-2 font-semibold">
        {visibleColumnIds.map((columnId) => {
          const column = columnsById.get(columnId);
          return (
            <TableCell
              key={columnId}
              className={`py-3 pr-4 pl-10 text-left ${
                column?.numeric ? "tabular-nums" : ""
              }`}
            >
              {column?.render(total)}
            </TableCell>
          );
        })}
      </TableRow>
    );
  };
  return (
    <DataTable
      entityLabelPlural="matters"
      columns={dataTableColumns}
      data={rows}
      getRowId={(row) => rowKey(row)}
      hidePagination={!serverPagination}
      hideSearchBar
      isLoading={isLoading}
      noResultsMessage={noResultsMessage}
      renderRowContextMenu={
        getMatterId
          ? (row, trigger) => {
              const matterId = getMatterId(row.original);
              return matterId ? (
                <MatterRowContextMenu matterId={matterId} trigger={trigger} />
              ) : (
                trigger
              );
            }
          : undefined
      }
      renderFooterRow={renderTotal}
      serverPagination={serverPagination}
      tableName={tableName}
    />
  );
}
export const count = (value: number | string | null) =>
  value === null
    ? "—"
    : Number(value).toLocaleString(undefined, { maximumFractionDigits: 1 });

const twoDecimalNumberFormatter = new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const twoDecimalCurrencyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export const decimal = (value: number | string | null) =>
  value === null ? "—" : twoDecimalNumberFormatter.format(Number(value));

export const moneyWithCents = (value: string | null) =>
  value === null ? "—" : twoDecimalCurrencyFormatter.format(Number(value));

export const money = (value: string | null) => {
  if (value === null) return "—";
  const negative = value.startsWith("-");
  const [whole, fraction = ""] = value.replace(/^-/, "").split(".");
  const rounded =
    BigInt(whole) +
    (Number(fraction.padEnd(2, "0").slice(0, 2)) >= 50 ? 1n : 0n);
  return `${negative && rounded !== 0n ? "-" : ""}$${rounded.toLocaleString("en-US")}`;
};
